import { css } from '@emotion/react';
import { Box, Stack, Typography } from '@mui/material';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormFactoryWithStandardLayout } from '../../../components/Form/FormFactory';
import { RendererType } from '../../../data-models/field-definition';
import { ISelectMeta } from '../../../data-models/field3.data-model';
import { createFormField, createFormFromFieldsArray } from '../../../view-models/form.view-model';
import { useAllChartOptions } from '../../CompanyProfiles/Performance/components/Charts/useKpiChartsMenu';
import { useKPIOptions } from '../../CompanyProfiles/Performance/components/Statistics/AddStatistic';
import {
  IKpiSelectItem,
  selectedKPIChartsOrgPrefAtom,
  selectedKpisOrgPrefAtom,
} from '../../CompanyProfiles/state/ProfileViewSettings';
import { DetailsConfigActions } from './DetailsConfigActions';
import { ExitConfigBlocker } from './ExitConfigBlocker';
import { ConfigStatus } from './useDisplayConfigState';

const panelContainer = css`
  display: grid;
  grid-template-columns: 1fr minmax(30%, 26rem);
  column-gap: 1rem;
  overflow: hidden;
`;

export function CompanySummaryPerformanceSettingsConfig() {
  const [editStatus, setEditStatus] = useState<ConfigStatus>('initial');
  const [selectedKpiPref, setSelectedKpiPref] = useAtom(selectedKpisOrgPrefAtom);
  const [selectedChartsPref, setSelectedChartsPref] = useAtom(selectedKPIChartsOrgPrefAtom);

  const setSelectedKpis = useCallback(
    (newValue: string[]) => {
      setSelectedKpiPref({ selectedFields: newValue });
    },
    [setSelectedKpiPref]
  );

  const setSelectedCharts = useCallback(
    (newValue: string[]) => {
      setSelectedChartsPref({ selectedFields: newValue });
    },
    [setSelectedChartsPref]
  );

  const methods = useForm<{
    statistics: string[];
    charts: string[];
  }>({
    mode: 'all',
    defaultValues: {
      statistics: selectedKpiPref.selectedFields,
      charts: selectedChartsPref.selectedFields,
    },
  });

  const isDirty = methods.formState.isDirty;

  useEffect(() => {
    if (isDirty && editStatus !== 'modified') {
      setEditStatus('modified');
    } else if (!isDirty && editStatus === 'modified') {
      setEditStatus('saved');
    }
  }, [editStatus, isDirty]);

  const onSave = async () => {
    await handleSubmit();
    setEditStatus('saved');
    methods.reset(methods.getValues());
  };

  const handleSubmit = useCallback(async () => {
    const values = methods.getValues();
    setSelectedKpis(values.statistics);
    setSelectedCharts(values.charts);
  }, [methods, setSelectedKpis, setSelectedCharts]);

  return (
    <FormProvider {...methods}>
      <Stack height='100%'>
        <ExitConfigBlocker onSave={handleSubmit} editStatus={editStatus} />
        <Stack direction='row' alignItems={'start'} columnGap='0.5rem'>
          <Typography variant='h4' mb='1rem'>
            Performance Settings
          </Typography>
        </Stack>
        <Stack direction={'row'} gap={'1rem'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography variant='body2' color='text.secondary'>
            Customize the selected fields for the Company Summary Performance settings section. To apply all
            changes, press the button &quot;Save Changes&quot;
          </Typography>
          <DetailsConfigActions save={{ handler: onSave, disabled: editStatus !== 'modified' }} />
        </Stack>
        <Box margin={'1rem 0'} css={panelContainer}>
          <CompanySummaryPerformanceSettingsForm />
        </Box>
      </Stack>
    </FormProvider>
  );
}

const CompanySummaryPerformanceSettingsForm = () => {
  const chartOptions = useAllChartOptions();
  const statisticsOptions = useKPIOptions();

  const form = useMemo(() => {
    const statisticsField = createFormField<ISelectMeta<string>>({
      key: 'statistics',
      label: 'Statistics',
      formatter: 'stringArray',
      renderer: RendererType.multiSelect,
      rendererMeta: {
        values: statisticsOptions.map(kpiSelectItemToOption),
      },
    });
    const chartsField = createFormField<ISelectMeta<string>>({
      key: 'charts',
      label: 'Charts',
      formatter: 'stringArray',
      renderer: RendererType.multiSelect,
      rendererMeta: {
        values: chartOptions.map(kpiSelectItemToOption),
      },
    });
    return createFormFromFieldsArray([statisticsField, chartsField]);
  }, [chartOptions, statisticsOptions]);

  return <FormFactoryWithStandardLayout form={form} />;
};

function kpiSelectItemToOption(item: IKpiSelectItem) {
  return {
    value: item.entityField,
    displayName: item.value,
    group: item.group,
  };
}
